import { useCallback, useMemo, useState } from 'react'

import MailIcon from '@mui/icons-material/Mail'
import PrintIcon from '@mui/icons-material/Print'
import SaveAltIcon from '@mui/icons-material/SaveAlt'
import SearchIcon from '@mui/icons-material/Search'
import { TextField } from '@mui/material'
import Box from '@mui/material/Box'
import Button, { ButtonProps } from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import {
  type GridColDef,
  type GridRenderCellParams,
  type GridFilterItem,
  type GridSortModel,
  type GridToolbarExportProps,
  useGridApiContext,
  GridToolbarContainer,
  GridToolbar,
} from '@mui/x-data-grid'
import ConfirmationModal from 'components/ConfirmationModal'
import DataTable, { ColumnItemProps } from 'components/DataTable'
import DatePicker from 'components/DatePicker'
import LoadingFullPageWithOverlay from 'components/LoadingFullPageWithOverlay'
import { ROUTES } from 'constants/routes'
import { useGlobalState } from 'contexts/global-state'
import { useQueryParams } from 'hooks/useQueryParams'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { ApiService } from 'services/api'
import { formatUtcToBrasilia } from 'utils/formatDate'
import { cpfCnpjMask, currencyMask } from 'utils/mask'
import { removeEmptyFieldsInObjectFieldsInObject } from 'utils/removeEmptyFieldsInObject'

import {
  SendEmailInvoiceErrorMap,
  ExportInvoicesXmlZipErrorMap,
} from '../../services/api/invoices-services/invoices-services-error-messages'

interface FormFilters {
  reservation_code?: string
  client: {
    email?: string
    document?: string
  }
}

export default function InvoicesServices() {
  const query = useQueryParams()
  const reactHookFormSearch = useForm<FormFilters>()

  const [startDate, setStartDate] = useState<string | Date | null | undefined>(
    null,
  )

  const [endDate, setEndDate] = useState<string | Date | null | undefined>(null)

  const [showExportXmlButton, setShowExportXmlButton] = useState(false)

  const [searchParams, setSearchParams] = useState<GenericObject[]>(() => {
    const situation = query.getAll('situation')
    const settlement_date_period = query.get('settlement_date_period')
    let start_travel_date_period
    if (
      ['waiting_payment', 'realized'].includes(situation[0]) &&
      ['waiting_payment', 'realized'].includes(situation[1])
    ) {
      const start = moment().format('YYYY-MM-DD')
      const end = moment().add(1, 'year').format('YYYY-MM-DD')

      start_travel_date_period = `${start}to${end}`
    }
    return [
      {
        situation,
        financialMovements: {
          settlement_date_period,
        },
        activity: {
          start_travel_date_period,
        },
      },
    ]
  })
  const [filterOptions, setFilterOptions] = useState<GridFilterItem[]>([])
  const [sortOptions, setSortOptions] = useState<GridSortModel>([
    { field: 'id', sort: 'desc' },
  ])

  const { openSuccessToast, openErrorToast } = useGlobalState()

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleFilter = useCallback(() => {
    const data = removeEmptyFieldsInObjectFieldsInObject(
      reactHookFormSearch.getValues() as unknown as GenericObject,
    ) as {
      reservation_code: string
      client: { document: string; email: string }
    }

    const start = startDate
      ? String(startDate).split('/').reverse().join('-')
      : ''
    const end = endDate ? String(endDate).split('/').reverse().join('-') : ''

    setSearchParams([
      {
        ...data,
        client: {
          ...data.client,
          document: data.client?.document?.length
            ? cpfCnpjMask(data.client?.document)
            : undefined,
        },
        issue_date_period: `${start}to${end}`,
      },
    ])

    if (
      (startDate && endDate) ||
      data.reservation_code ||
      data.client.email ||
      data.client.document
    ) {
      setShowExportXmlButton(true)
    }
  }, [endDate, reactHookFormSearch, startDate])

  const [modalSendInvoice, setModalSendInvoice] = useState<{
    isActive: boolean
    selectedInvoice?: number
  }>({
    isActive: false,
    selectedInvoice: undefined,
  })

  const onConfirmModalSendInvoice = useCallback(async () => {
    if (modalSendInvoice.selectedInvoice) {
      setIsLoading(true)
      await ApiService.InvoicesServices.sendEmailInvoice({
        id: String(modalSendInvoice.selectedInvoice),
      })
        .then(() => {
          handleFilter()
          openSuccessToast({
            message: 'Nota fiscal enviada com sucesso!',
          })
        })
        .catch((error) => {
          let message =
            'Não foi possível enviar a nota fiscal. Tente novamente!'

          const apiErrorMessage = error.message as string

          if (SendEmailInvoiceErrorMap[apiErrorMessage]) {
            message = SendEmailInvoiceErrorMap[apiErrorMessage]
          }

          openErrorToast({
            message,
          })
        })
    }
    setIsLoading(false)
    setModalSendInvoice({
      isActive: false,
      selectedInvoice: undefined,
    })
  }, [
    handleFilter,
    modalSendInvoice.selectedInvoice,
    openErrorToast,
    openSuccessToast,
  ])

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'reservation_id',
        headerName: 'Id Reserva',
        flex: 1,
        hide: true,
      },
      {
        field: 'client.fantasy_name',
        headerName: 'Cliente',
        flex: 1,
        hide: true,
        relation: {
          name: 'invoiceService.client',
          alias: 'client',
        },
      },
      {
        field: 'reservation_code',
        headerName: 'Código Reserva',
        flex: 1,
      },
      {
        field: 'activity_name',
        headerName: 'Atividade',
        flex: 1,
      },
      {
        field: 'client_name',
        headerName: 'Cliente',
        flex: 1,
      },
      {
        field: 'student_name',
        headerName: 'Aluno',
        flex: 1,
      },
      {
        field: 'number',
        headerName: 'Nº nota fiscal',
        flex: 1,
      },
      {
        field: 'total_amount',
        headerName: 'Valor',
        flex: 1,
        valueFormatter: ({ value }) => currencyMask(value),
      },
      {
        field: 'issue_date',
        headerName: 'Data de Emissão',
        flex: 1,
        valueGetter: ({ value }) => formatUtcToBrasilia(value as string),
      },
      {
        field: 'email_sent_at',
        headerName: 'Data de Envio',
        flex: 1,
      },
      {
        field: 'actions',
        headerName: 'Ações',
        width: 110,
        sortable: false,
        exclude: true,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <>
              <div
                style={{ all: 'unset', cursor: 'pointer', marginRight: '10px' }}
                onClick={() => {
                  setModalSendInvoice({
                    isActive: true,
                    selectedInvoice: params.row.id,
                  })
                }}
              >
                <MailIcon />
              </div>
              <Link
                style={{ all: 'unset', cursor: 'pointer', marginRight: '10px' }}
                to={`https://nfse.turismoestudantil.com.br/app/nFse/imprimirRps/${params.row.id}`}
              >
                <PrintIcon />
              </Link>
              <Link
                style={{ all: 'unset', cursor: 'pointer' }}
                to={ROUTES.RESERVATIONS.VIEW(params.row.reservation_id)}
              >
                <SearchIcon />
              </Link>
            </>
          )
        },
      },
    ],
    [],
  )

  const loadAllInvoices = useCallback(async () => {
    const entity = 'InvoiceService'
    const source = 'invoiceService'

    const newParams = {
      entity,
      source,
      page: 1,
      perPage: 0,
      searchParameters: searchParams,
      likeParameters: filterOptions,
      sortParameters: sortOptions,
      selectFields: columns
        .filter((column: ColumnItemProps) => !column.exclude)
        .map((column: ColumnItemProps) => {
          const field = column.customField || column.field
          if (!column.relation) {
            return `${source}.${field}`
          }
          return field
        }),
      relations: columns
        .filter((column: ColumnItemProps) => !!column.relation)
        .map((column: ColumnItemProps) => ({
          ...column.relation,
        })),
    }
    const response = await ApiService.DataTable.getAll(newParams)
    return response.items
  }, [columns, filterOptions, searchParams, sortOptions])

  const generateExportXml = useCallback(async () => {
    const invoices = (await loadAllInvoices()) as { id: number }[]

    try {
      await ApiService.InvoicesServices.exportInvoicesXmlZip({
        ids: invoices.map((invoice) => invoice.id),
        fileName: `notas_fiscais_${Date.now()}`,
      })
    } catch (error: unknown) {
      let message =
        'Não foi possível exportar as notas fiscais. Tente novamente!'

      const apiErrorMessage = (error as ApiError).message as string

      if (ExportInvoicesXmlZipErrorMap[apiErrorMessage]) {
        message = ExportInvoicesXmlZipErrorMap[apiErrorMessage]
      }

      openErrorToast({
        message,
      })
    }
  }, [loadAllInvoices, openErrorToast])

  const handleExportXml = useCallback(async () => {
    try {
      setIsLoading(true)
      await generateExportXml()
    } catch (error: unknown) {
      setIsLoading(false)
      openErrorToast({
        message: `Ocorreu um erro ao baixar xml: ${
          (error as ApiError).message
        }`,
      })
    }
    setIsLoading(false)
  }, [generateExportXml, openErrorToast])

  function CustomToolbar(props: GridToolbarExportProps) {
    const { user, openErrorToast } = useGlobalState()
    const apiRef = useGridApiContext()

    const generateExportCsv = useCallback(async () => {
      const oldRows = apiRef.current.getSortedRows()
      const invoices = (await loadAllInvoices()) as { id: number }[]
      apiRef.current.setRows(invoices)
      apiRef.current.exportDataAsCsv({
        ...props.csvOptions,
        utf8WithBom: true,
        delimiter: ';',
      })
      apiRef.current.setRows(oldRows)
    }, [apiRef, props.csvOptions])

    const handleExport = useCallback(async () => {
      try {
        const data = removeEmptyFieldsInObjectFieldsInObject(
          reactHookFormSearch.getValues() as unknown as GenericObject,
        ) as {
          reservation_code: string
          client: { document: string; email: string }
        }

        const start = startDate
          ? String(startDate).split('/').reverse().join('-')
          : ''
        const end = endDate
          ? String(endDate).split('/').reverse().join('-')
          : ''

        await ApiService.AuditLogs.create({
          entity: 'InvoiceService',
          type: 'export',
          changes: JSON.stringify([
            {
              valueAfter: {
                client: {
                  ...data.client,
                  document: data.client?.document?.length
                    ? cpfCnpjMask(data.client?.document)
                    : undefined,
                },
                issue_date_period: `${start}to${end}`,
              },
            },
          ]),
          client_id: null,
          entity_id: 0,
          user_id: user?.id,
        })
        generateExportCsv()
      } catch (error: unknown) {
        openErrorToast({
          message: `Ocorreu um erro ao criar um registro na auditoria: ${
            (error as ApiError).message
          }`,
        })
      }
    }, [generateExportCsv, openErrorToast, user?.id])

    const buttonBaseProps: ButtonProps = {
      color: 'primary',
      size: 'small',
      startIcon: <SaveAltIcon />,
    }
    return (
      <GridToolbarContainer>
        <GridToolbar
          csvOptions={{ disableToolbarButton: true }}
          printOptions={{ disableToolbarButton: true }}
        />
        <Button {...buttonBaseProps} onClick={() => handleExport()}>
          Exportar
        </Button>
      </GridToolbarContainer>
    )
  }

  return (
    <>
      {isLoading && <LoadingFullPageWithOverlay />}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 8,
        }}
      >
        <Container maxWidth={false}>
          <Box>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <Typography sx={{ m: 1 }} variant="h4">
                Notas fiscais
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'flex-start',
              flexWrap: 'wrap',
              gap: 1,
              marginTop: '20px',
            }}
          >
            <TextField
              label="Código"
              type="text"
              variant="outlined"
              {...reactHookFormSearch.register('reservation_code')}
              onChange={() => {
                setShowExportXmlButton(false)
              }}
            />
            <TextField
              label="Email"
              type="email"
              variant="outlined"
              {...reactHookFormSearch.register('client.email')}
              onChange={() => {
                setShowExportXmlButton(false)
              }}
            />
            <TextField
              label="CPF"
              type="document"
              variant="outlined"
              {...reactHookFormSearch.register('client.document')}
              onChange={() => {
                setShowExportXmlButton(false)
              }}
            />

            <DatePicker
              label="Data inicial"
              id="startDate"
              value={startDate}
              onChange={(newValue) => {
                setStartDate(newValue)
                setShowExportXmlButton(false)
              }}
            />

            <DatePicker
              label="Data final"
              id="endDate"
              value={endDate}
              onChange={(newValue) => {
                setEndDate(newValue)
                setShowExportXmlButton(false)
              }}
            />

            <Button color="primary" variant="contained" onClick={handleFilter}>
              Pesquisar
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                setSearchParams([])
                setShowExportXmlButton(false)
              }}
            >
              Todos
            </Button>
            {showExportXmlButton && (
              <Button
                color="secondary"
                variant="contained"
                onClick={() => handleExportXml()}
              >
                Exportar xml
              </Button>
            )}
          </Box>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <DataTable
                entity="InvoiceService"
                source="invoiceService"
                columns={columns}
                onFilterModelChange={(newValue) => {
                  if (newValue.items[0]?.value !== undefined) {
                    const copy = newValue.items
                    setFilterOptions(copy)
                  }
                }}
                onSortModelChange={(newValue) => {
                  setSortOptions(newValue)
                }}
                searchParameters={searchParams}
                likeParameters={filterOptions}
                sortParameters={sortOptions}
                customToolbar={() => <CustomToolbar />}
                disableColumnFilter
              />
            </CardContent>
          </Card>
        </Container>
      </Box>
      <ConfirmationModal
        title={`Enviar email com nota fiscal?`}
        opened={modalSendInvoice.isActive}
        onClose={() =>
          setModalSendInvoice({
            isActive: false,
            selectedInvoice: undefined,
          })
        }
        onConfim={onConfirmModalSendInvoice}
        onCancel={() =>
          setModalSendInvoice({
            isActive: false,
            selectedInvoice: undefined,
          })
        }
        loading={isLoading}
      ></ConfirmationModal>
    </>
  )
}
